import _, { orderBy, sortBy } from 'lodash';
import type { ItemGroup } from 'src/pages/organization-page/organization-page-view-model.js';
import type {
  OverviewPageViewModel,
  OverviewSection,
  PageLink,
  PageLinks,
} from 'src/pages/overview-page/overview-page-view-model.js';
import type {
  FunctionViewModel,
  IssueViewModel,
  RiskAssessmentViewModel,
  SubstanceViewModel,
} from '@trinnvis/dabih-api-client';
import { commonFieldsByCode } from 'src/store/fields.js';
import type { EmployeeViewModelWithName, OrganizationState, State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { calculateUpdateStatus } from '../resolvers/calculate-update-status.js';
import { riskLevelFromRisk } from 'src/models/risk.js';
import { entityNameForLists } from 'src/utilities/text';
import { t } from 'src/i18n';
import {
  currentContactUuid,
  functionsNotDeleted,
  getPeopleForAccessControl,
  substancesNotDeleted,
  suggestedReports,
  suggestedReportsForPage,
  type SuggestedRiskAssessment,
  suggestedRiskAssessmentsForEmergency,
} from 'src/store/selectors';
import type { AbstractPageView } from 'src/pages/abstract-page-view';
import { getHsePlan, storedCollections } from 'src/store/selectors/collections';
import { secondaryLabelForTutorial } from './tutorial-list-helpers';
import { documentsNotDeleted } from '../../store/selectors/documents/documents-not-deleted';
import { guidelinesNotDeleted } from '../../store/selectors/documents/guidelines-not-deleted';
import { reportsNotDeleted } from '../../store/selectors/documents/reports-not-deleted';
import { contractsNotDeletedWithParties } from '../../store/selectors/documents/contracts-not-deleted-with-parties';
import { currentEmployeeUuid } from '../../store/selectors/employees/current-employee-uuid';
import { employeesNotDeleted } from '../../store/selectors/employees/employees-not-deleted';
import { issuesNotDeleted } from '../../store/selectors/issues/issues-not-deleted';
import { isIssueProcessed } from '../../store/selectors/issues/is-issue-processed';
import type { IssueViewModelExtended } from '../../store/selectors/issues/issue-view-model-extended';
import { toIssueViewModel } from '../../store/selectors/issues/to-issue-view-model';
import { issueEscalationCandidates } from '../../store/selectors/issues/issue-escalation-candidates';
import { issueEscalationSettings } from '../../store/selectors/issues/issue-escalation-settings';
import { getOrganization } from '../../store/selectors/organization/getOrganization';
import { getOrganizationId } from '../../store/selectors/organization/getOrganizationId';
import { now } from '../../store/selectors/organization/now';
import { partnersNotDeleted } from '../../store/selectors/partners/partners-not-deleted';
import { functionsWithTasksForCurrentPage } from '../../store/selectors/responsibilities/functions-with-tasks-for-current-page';
import { tasksForCurrentPage } from '../../store/selectors/responsibilities/tasks-for-current-page';
import { startTasksForCurrentPage } from '../../store/selectors/responsibilities/start-tasks-for-current-page';
import { riskAssessmentsReducedByName } from '../../store/selectors/risk-assessments/risk-assessments-reduced-by-name';
import type { BaseListItem } from './base-list-item';
import type { RiskLevels } from '../../library/lists/riskLevels';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import { groupBy } from '../../store/selectors/utilities/groupBy';
import { pageIds } from '../../store/selectors/pages/pageIds';
import { getPages } from '../../store/selectors/pages/getPages';
import { getPage } from '../../store/selectors/pages/getPage';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { hseManagerUuid } from 'src/store/selectors/responsibilities/hse-manager-uuid';
import { sortedByLabel } from 'src/store/utilities';
import { suggestionEntityTypes } from 'src/store/selectors/suggestion-entity-types';
import { getListSectionMainIcon } from 'src/store/selectors/get-list-section-main-icon';
import type { SuggestedReport } from 'src/store/selectors/suggestedReport';
import { tutorialsForOrganization } from 'src/store/selectors/tutorials-for-organization';
import { emergencyAccessList } from 'src/store/selectors/emergency/emergency-access-list';
import { emergencyAccessPeople } from 'src/models/pages/emergency-access-people';
import { emergencyAccess } from 'src/store/selectors/emergency/emergency-access';
import { currentUserIsOwner } from 'src/store/selectors/employees/current-user-is-owner';

function filterByPage<T extends BaseListItem>(value: Array<T>, pageId: number) {
  return sortBy(
    value.filter((item) => {
      if (!item.pages) {
        return false;
      }
      return item.pages.includes(pageId);
    }),
    [(item) => item.name],
  ).map((item) => {
    let riskAssessmentRisk: RiskLevels = undefined;
    if (item.consequence && item.probability) {
      riskAssessmentRisk = riskLevelFromRisk(item.consequence * item.probability);
    }
    let relatedSubstance: string | undefined = undefined;
    if (item.relatedSubstance) {
      relatedSubstance = item.relatedSubstance;
    }
    return {
      name: item.nameWithParties || item.name,
      formSubtitle: item.formSubtitle ?? '',
      uuid: item.uuid,
      unchangedTemplate: item.unchangedTemplate,
      templateDeleted: item.templateDeleted,
      hasTemplateUpdate: item.hasTemplateUpdate,
      templateUpdated: item.templateUpdated,
      contentLastModified: item.contentLastModified,
      templateDeclined: item.templateDeclined,
      partners: item.partners ?? [],
      employees: item.employees ?? [],
      tasks: item.tasks ?? [],
      reportDate: item.reportDate,
      classification: item.classification ?? 'NONE',
      accessControl: item.accessControl ?? [],
      taskCount: item.taskCount,
      riskAssessmentRisk,
      relatedSubstance,
      hasDraft: item.hasDraft,
      isConfirmedEntity: item.isConfirmedEntity,
      draftName: item.draftName ?? '',
    };
  });
}

function addSuggestedReports(
  items: ListSectionItemInput[],
  suggestedReports: SuggestedReport[],
): ListSectionItemInput[] {
  if (suggestedReports.length) {
    const suggestions = sortBy(suggestedReports, (e) => e.name);
    const suggestionItems: ListSectionItemInput[] = suggestions.map((s) => {
      return {
        label: s.name,
        accessible: true,
        suggestion: true,
        clickData: s.pageId + '',
      };
    });
    return suggestionItems.concat(items);
  }
  return items;
}

function addListIfNotEmpty<T extends BaseListItem>(
  lists: OverviewSection[],
  type: string,
  source: Array<T>,
  pageId: number,
  hrefPrefix: string,
  currentEmployeeUuid: string | undefined,
  substances: SubstanceViewModel[] = [],
  suggestedReports: SuggestedReport[] = [],
) {
  let filtered = filterByPage(source, pageId);
  if (type === 'reports') {
    filtered = orderBy(filtered, [(i) => i.name, (i) => i.reportDate], ['asc', 'desc']);
  } else if (type === 'riskAssessments') {
    filtered = orderBy(
      filtered,
      [(item) => item.unchangedTemplate, (item) => item.riskAssessmentRisk, (item) => item.name],
      ['asc', 'desc', 'asc'],
    );
  }
  let items: ListSectionItemInput[] = filtered.map((g) => {
    let label = entityNameForLists(g.name, g.draftName, g.isConfirmedEntity);
    let secondaryLabel = '';
    if (type === 'reports') {
      const secondaryLabelElms: string[] = [];
      if (g.formSubtitle) {
        secondaryLabelElms.push(g.formSubtitle);
      }
      if (g.reportDate) {
        secondaryLabelElms.push(LocalDate.fromString(g.reportDate).toStringForDisplay());
      }
      secondaryLabel = secondaryLabelElms.join(' ');
    }
    if (type === 'riskAssessments' && g.relatedSubstance) {
      const substance = substances.find((s) => {
        return s.uuid === g.relatedSubstance;
      });
      if (substance) {
        label = substance.name + ': ' + label;
      }
    }
    let href = hrefPrefix + type + '/' + g.uuid;
    if (!g.isConfirmedEntity || !g.name || (suggestionEntityTypes.includes(type) && g.unchangedTemplate)) {
      href += '?edit';
    }
    return {
      mainIcon: getListSectionMainIcon(type, g.unchangedTemplate, g.classification, g.riskAssessmentRisk),
      accessible: g.classification === 'NONE' || (g.accessControl ?? []).includes(currentEmployeeUuid ?? ''),
      updateStatus: type === 'partners' ? 'none' : calculateUpdateStatus(g, LocalDate.now()),
      hasDraft: g.hasDraft,
      label,
      secondaryLabel,
      href,
      riskAssessmentRisk: g.riskAssessmentRisk,
    };
  });
  if (type === 'reports') {
    items = addSuggestedReports(items, suggestedReports);
  }
  if (items.length) {
    const notSuggestions: ListSectionItemInput[] = [];
    const suggestions: ListSectionItemInput[] = [];
    items.forEach((item) => {
      if (item.mainIcon === 'suggestion') {
        suggestions.push(item);
      } else {
        notSuggestions.push(item);
      }
    });
    const field = 'organization_' + type;
    const label = '';
    const secondaryLabel = '';
    lists.push({
      type: 'list',
      icon: type,
      field,
      label,
      secondaryLabel,
      items: notSuggestions.concat(suggestions),
    });
  }
}

function addContractsListIfNotEmpty<T extends BaseListItem>(
  lists: OverviewSection[],
  source: Array<T>,
  pageId: number,
  hrefPrefix: string,
  currentEmployeeUuid: string | undefined,
  employees: EmployeeViewModelWithName[],
) {
  const pageContracts = filterByPage(source, pageId);
  const contractsWithActiveParties = pageContracts.filter((c) => {
    let include = false;
    if (c.partners.length || !c.employees.length) {
      include = true;
    } else {
      c.employees.forEach((employeeUuid) => {
        const employee = employees.find((employee) => {
          return employee.uuid === employeeUuid;
        });
        if (employee && employee.status !== 'TERMINATED') {
          include = true;
        }
      });
    }
    return include;
  });
  if (contractsWithActiveParties.length > 0) {
    const items: ListSectionItemInput[] = contractsWithActiveParties.map((g) => {
      let href = hrefPrefix + 'contracts/' + g.uuid;
      if (!g.isConfirmedEntity || !g.name || g.unchangedTemplate) {
        href += '?edit';
      }
      return {
        mainIcon: getListSectionMainIcon('contracts', g.unchangedTemplate, g.classification, g.riskAssessmentRisk),
        accessible: g.classification === 'NONE' || (g.accessControl ?? []).includes(currentEmployeeUuid ?? ''),
        updateStatus: calculateUpdateStatus(g, LocalDate.now()),
        hasDraft: g.hasDraft,
        label: entityNameForLists(g.name, g.draftName, g.isConfirmedEntity),
        secondaryLabel: '',
        href,
        riskAssessmentRisk: g.riskAssessmentRisk,
      };
    });
    const notSuggestions = sortedByLabel(
      items.filter((item) => {
        return item.mainIcon !== 'suggestion';
      }),
    );
    const suggestions = sortedByLabel(
      items.filter((item) => {
        return item.mainIcon === 'suggestion';
      }),
    );
    if (pageContracts.length > contractsWithActiveParties.length) {
      const hrefArr = hrefPrefix.split('/');
      hrefArr.length = 3;
      const href = hrefArr.join('/') + '/' + pageIds.employees;
      suggestions.push({
        label: t('overview.contractWithTerminatedStaffInfo'),
        accessible: true,
        href,
        mainIcon: 'info',
      });
    }
    lists.push({
      type: 'list',
      icon: 'contracts',
      field: 'organization_contracts',
      items: notSuggestions.concat(suggestions),
    });
  }
}

function addRiskAssessmentsForEmergency(
  lists: OverviewSection[],
  riskAssessments: RiskAssessmentViewModel[],
  suggestedRiskAssessments: SuggestedRiskAssessment[],
  hrefPrefix: string,
  emergencyAccess: boolean,
) {
  const filtered = filterByPage(riskAssessments, pageIds.emergency);
  const notSuggestions: ListSectionItemInput[] = filtered.map((item) => {
    let href = hrefPrefix + 'riskAssessments/' + item.uuid;
    if (!item.isConfirmedEntity || !item.name) {
      href += '?edit';
    }
    return {
      mainIcon: getListSectionMainIcon(
        'riskAssessments',
        item.unchangedTemplate,
        item.classification,
        item.riskAssessmentRisk,
      ),
      accessible: true,
      label: entityNameForLists(item.name, item.draftName, item.isConfirmedEntity),
      href,
    };
  });
  const createdFromTemplateIds: number[] = riskAssessments
    .filter((item) => {
      return item.pages?.includes(pageIds.emergency);
    })
    .map((item) => {
      return item.createdFromTemplate ?? 0;
    });
  const suggestions: ListSectionItemInput[] = suggestedRiskAssessments
    .filter((item) => {
      return !createdFromTemplateIds.includes(item.pageId);
    })
    .map((item) => {
      return {
        suggestion: true,
        mainIcon: 'suggestion',
        accessible: emergencyAccess,
        label: item.name,
        clickData: item.pageId + '',
      };
    });
  lists.push({
    type: 'list',
    icon: 'riskAssessments',
    label: t('overview.emergencySituations'),
    secondaryLabel: t('overview.riskAndActionCards'),
    items: sortedByLabel(notSuggestions).concat(sortedByLabel(suggestions)),
  });
}

function sortedByDate(list: IssueViewModel[]) {
  return _sortedByDate1(list, false);
}

function _sortedByDate1(list: IssueViewModel[], reverse: boolean) {
  const r = reverse ? 'desc' : 'asc';
  return _.orderBy(list, [(item) => item.reportedDate, (item) => item.name], [r, r]);
}

/**
 *
 * @param value
 * @param pageId Use pageId 0 for all issues
 */
function filterIssuesByPage(value: IssueViewModel[], pageId: number) {
  const list = value.filter(function (item) {
    if (!item.pages) {
      return false;
    }
    return item.pages.includes(pageId) || pageId === 0;
  });

  return sortedByDate(list);
}

function issueItems(issues: IssueViewModelExtended[], pageId: number): IssueViewModelExtended[] {
  return filterIssuesByPage(issues, pageId).map((i) => {
    return {
      ...i,
      reportedDateFormatted: _formatDate(i.reportedDate),
      status: isIssueProcessed(i) ? 'ferdigbehandlet' : 'aktiv',
    };
  });
}

function _workRelatedHealthIssues(
  issues: IssueViewModelExtended[],
  currentEmployeeUuid: string | undefined,
  employees: EmployeeViewModelWithName[],
): IssueViewModelExtended[] {
  return issueItems(issues, 0)
    .filter((item) => item.workRelatedHealthIssue)
    .map((issue) => {
      let displayName: string;

      if (
        issue.classification &&
        issue.classification !== 'NONE' &&
        issue.accessControl &&
        currentEmployeeUuid !== undefined &&
        issue.accessControl.includes(currentEmployeeUuid)
      ) {
        displayName = issue.name ?? '';
      } else {
        const workRelatedHealthIssueEmployees = employees.filter(
          (employee) => employee.uuid === issue.workRelatedHealthIssueEmployee,
        );
        if (workRelatedHealthIssueEmployees.length) {
          displayName = workRelatedHealthIssueEmployees[0].name;
        } else {
          displayName = t('overview.nameNotProvided');
        }
      }

      return {
        ...issue,
        displayName: displayName,
      };
    });
}

function _issues(issues: IssueViewModelExtended[], pageId: number) {
  return issueItems(issues, pageId);
}

function _otherHmsIssues(issues: IssueViewModelExtended[]) {
  return issueItems(issues, 63).filter((item) => !item.workRelatedHealthIssue);
}

function _formatDate(value: string | undefined) {
  if (value) {
    return LocalDate.fromString(value).toStringForDisplay();
  }
  return t('overview.withoutDate');
}

function tutorialsAsListItems(
  organization: OrganizationState,
  hrefPrefix: string,
  pageId: number,
): ListSectionItemInput[] {
  return tutorialsForOrganization(organization)
    .filter((t) => {
      return t.pages.includes(pageId);
    })
    .map((t) => {
      return {
        label: t.title,
        secondaryLabel: secondaryLabelForTutorial(t),
        accessible: true,
        updateStatus: 'none',
        href: hrefPrefix + 'tutorials/' + t.id,
      };
    });
}

function addTutorialsList(
  organization: OrganizationState,
  lists: OverviewSection[],
  hrefPrefix: string,
  pageId: number,
) {
  lists.push({
    type: 'list',
    icon: 'competencies',
    label: t('library.tutorials.coursesAndGuides'),
    items: sortBy(tutorialsAsListItems(organization, hrefPrefix, pageId), (e) => e.label),
  });
}

/**
 * Grouped view of issues based on status. The issues that are "done" are shown in a filtered group.
 *
 *
 * @param issues
 */
function toGroupedIssues(
  hrefPrefix: string,
  issues: IssueViewModelExtended[],
  currentEmployeeUuid: string,
): ItemGroup[] {
  const groupNames = {
    ferdigbehandlet: t('overview.issuesProcessedGroup'),
    aktiv: t('overview.issuesActiveGroup'),
  };

  const groupedEmployees = groupBy(issues, (e) => e.status ?? '');

  const result: ItemGroup[] = _.sortBy(groupedEmployees, [(g) => g.group !== 'aktiv', (g) => g.group]).map((g) => {
    return {
      label: groupNames[g.group],
      tooltip: commonFieldsByCode()['issues_closed'].tooltip,
      filtered: g.group === 'ferdigbehandlet',
      items: _.sortBy(g.items, (e) => e.reportedDate).map((e) => {
        const classification = e.classification ?? 'NONE';
        const accessControl = e.accessControl ?? [];
        return {
          uuid: e.uuid,
          label: entityNameForLists(e.name, e.draftName, e.isConfirmedEntity),
          dateDisplay: e.reportedDateFormatted,
          accessControl: accessControl,
          classification: classification,
          locked: classification !== 'NONE',
          accessible: classification === 'NONE' || accessControl.includes(currentEmployeeUuid),
          href: hrefPrefix + 'issues/' + e.uuid,
          relatedItems: [],
          hasDraft: e.hasDraft,
        };
      }),
    };
  });

  return result;
}

export function overviewPageView(hrefPrefix: string, viewModel: AbstractPageView, state: State): OverviewPageViewModel {
  const employeeUuid = currentEmployeeUuid(state);
  const stickyLists: OverviewSection[] = [];
  const otherLists: OverviewSection[] = [];

  const startTasks = startTasksForCurrentPage(state);
  if (startTasks.length > 0) {
    stickyLists.push({
      type: 'start-tasks',
      multipleEmployees: employeesNotDeleted(state).length > 1,
      items: startTasks.map((task) => ({
        label: task.timeDisplay,
        secondaryLabel: task.name,
        eventCurrentUser: task.myTask,
        href: task.href,
        rightLabel: (task.myTask ? t('overview.assigneeMe') : task.employeeName) + ' (' + task.functionName + ')',
        rightLabelClass: 'assignee',
        eventDone: task.executed,
        accessible: !task.executed,
        uuid: task.uuid,
      })),
    });
  }

  if (getFeatureStates(state).collections) {
    addListIfNotEmpty(otherLists, 'collections', storedCollections(state), viewModel.pageId, hrefPrefix, employeeUuid);
  }

  const functionsWithTasks = functionsWithTasksForCurrentPage(state);
  const tasks = tasksForCurrentPage(state);

  if (functionsWithTasks.length > 0 || tasks.length > 0) {
    // d-list-section-page-tasks
    otherLists.push({
      type: 'page-tasks',
      functionsWithTasks: functionsWithTasks.map((f) => {
        let href = hrefPrefix + 'functions/' + f.uuid;
        if (!f.isConfirmedEntity) {
          href += '?edit';
        }
        return {
          updateStatus: calculateUpdateStatus(f, LocalDate.fromString(state.today)),
          assignedNames: f.assignedNames,
          name: entityNameForLists(f.name, f.draftName, f.isConfirmedEntity),
          href,
          hasDraft: f.hasDraft,
          tasks: f.tasks.map((t) => {
            let href = hrefPrefix + 'tasks/' + t.uuid;
            if (!t.isConfirmedEntity) {
              href += '?edit';
            }
            return {
              name: entityNameForLists(t.name, t.draftName, t.isConfirmedEntity),
              updateStatus: calculateUpdateStatus(t, now(state)),
              hasDraft: t.hasDraft,
              href,
            };
          }),
        };
      }),
      tasks: tasks.map((t) => {
        let href = hrefPrefix + 'tasks/' + t.uuid;
        if (!t.isConfirmedEntity) {
          href += '?edit';
        }
        return {
          name: entityNameForLists(t.name, t.draftName, t.isConfirmedEntity),
          updateStatus: calculateUpdateStatus(t, now(state)),
          hasDraft: t.hasDraft,
          href,
        };
      }),
    });
  }
  const organization = getOrganization(state);
  if (organization === undefined) {
    throw new Error('Illegal state (E152), organization not found');
  }
  addTutorialsList(
    organization,
    viewModel.pageId === pageIds.emergency ? stickyLists : otherLists,
    hrefPrefix,
    viewModel.pageId,
  );

  addListIfNotEmpty(otherLists, 'guidelines', guidelinesNotDeleted(state), viewModel.pageId, hrefPrefix, employeeUuid);
  addListIfNotEmpty(otherLists, 'documents', documentsNotDeleted(state), viewModel.pageId, hrefPrefix, employeeUuid);
  addContractsListIfNotEmpty(
    otherLists,
    contractsNotDeletedWithParties(state),
    viewModel.pageId,
    hrefPrefix,
    employeeUuid,
    employeesNotDeleted(state),
  );
  addListIfNotEmpty(otherLists, 'partners', partnersNotDeleted(state), viewModel.pageId, hrefPrefix, employeeUuid);

  addListIfNotEmpty(
    otherLists,
    'reports',
    reportsNotDeleted(state),
    viewModel.pageId,
    hrefPrefix,
    employeeUuid,
    [],
    suggestedReportsForPage(suggestedReports(state), viewModel.pageId),
  );

  const issues = issuesNotDeleted(state).map((i) => toIssueViewModel(i));

  if (viewModel.pageId === pageIds.hse) {
    const escalationSettings = issueEscalationSettings(state);
    const escalationCandidates = issueEscalationCandidates(state, hrefPrefix);
    if (escalationSettings.enabled && escalationCandidates.length > 0) {
      otherLists.push({
        type: 'issue-escalation',
        candidates: escalationCandidates,
        enabled: escalationSettings.enabled,
        href: hrefPrefix + 'issues',
      });
    }
    const employees = employeesNotDeleted(state);
    const viewerEmployeeUuid = currentEmployeeUuid(state);
    const workRelatedHealthIssues = _workRelatedHealthIssues(issues, viewerEmployeeUuid, employees);
    if (workRelatedHealthIssues.length > 0) {
      otherLists.push({
        type: 'issues-grouped',
        field: 'issues_workRelatedHealthIssueTitle',
        items: toGroupedIssues(hrefPrefix, workRelatedHealthIssues, employeeUuid ?? ''),
      });
    }
    const otherHmsIssues = _otherHmsIssues(issues);
    if (otherHmsIssues.length > 0) {
      otherLists.push({
        type: 'issues-grouped',
        field: 'issues_otherHmsIssuesTitle',
        items: toGroupedIssues(hrefPrefix, otherHmsIssues, employeeUuid ?? ''),
      });
    }
  } else {
    const issueItems = _issues(issues, viewModel.pageId);
    if (issueItems.length > 0) {
      otherLists.push({
        type: 'issues-grouped',
        field: 'organization_issues',
        items: toGroupedIssues(hrefPrefix, issueItems, employeeUuid ?? ''),
      });
    }
  }

  if (viewModel.pageId === pageIds.emergency) {
    addRiskAssessmentsForEmergency(
      stickyLists,
      riskAssessmentsReducedByName(state),
      suggestedRiskAssessmentsForEmergency(state),
      hrefPrefix,
      emergencyAccess(state),
    );
  } else {
    addListIfNotEmpty(
      otherLists,
      'riskAssessments',
      riskAssessmentsReducedByName(state),
      viewModel.pageId,
      hrefPrefix,
      employeeUuid,
      substancesNotDeleted(state),
    );
  }

  const pageLinkItems: PageLink[] = [];
  if (viewModel.pageId === pageIds.hse) {
    if (viewModel.featureStates.hse && !viewModel.featureStates.collections) {
      const hsePlan = getHsePlan(state);
      if (hsePlan) {
        pageLinkItems.push({
          type: 'collections',
          name: hsePlan.name,
          href: '/account/' + getOrganizationId(state) + '/' + pageIds.hse + '/collections/' + hsePlan.uuid,
        });
      }
    }
    const page = getPage(pageIds.substances, getPages(state));
    if (page) {
      pageLinkItems.push({
        type: 'substances',
        name: page?.name ?? '',
        href: '/account/' + getOrganizationId(state) + '/' + page.pageId,
      });
    }
  }
  if (viewModel.pageId === pageIds.assetManagement) {
    const page = getPage(pageIds.assets, getPages(state));
    pageLinkItems.push({
      type: 'assets',
      name: page?.name ?? '',
      href: '/account/' + getOrganizationId(state) + '/' + page?.pageId || '',
    });
  }
  if (viewModel.pageId === pageIds.informationSecurity) {
    const page = getPage(pageIds.dataSystems, getPages(state));
    pageLinkItems.push({
      type: 'computers',
      name: page?.name ?? '',
      href: '/account/' + getOrganizationId(state) + '/' + page?.pageId || '',
    });
  }
  let lists: OverviewSection[] = [...stickyLists, ...otherLists];
  if (pageLinkItems.length) {
    const pageLinks: PageLinks = {
      type: 'page-links',
      items: pageLinkItems,
    };
    lists = [...stickyLists, pageLinks, ...otherLists];
  }

  let defaultFunctionForPage: FunctionViewModel | undefined = undefined;
  if (viewModel.pageId === pageIds.hse) {
    defaultFunctionForPage = functionsNotDeleted(state).find((f) => {
      return f.uuid === hseManagerUuid(state);
    });
  }

  return {
    ...viewModel,
    type: 'overview-page',
    href: hrefPrefix,
    defaultFunctionForPage,
    currentEmployeeUuid: currentEmployeeUuid(state),
    currentContactUuid: currentContactUuid(state),
    suggestedReports: suggestedReportsForPage(suggestedReports(state), viewModel.pageId),
    lists,
    // Eier kan alltid redigere beredskapstilgangslisten (speiler
    // currentUserCanEditStaffingAccess). Uten dette kunne en organisasjon med
    // tom emergencyAccessList bli låst: ingen ville kunne gi seg selv eller
    // andre tilgang. Selve innholdstilgangen (currentUserHasEmergencyAccess)
    // er fortsatt rent listebasert – eier får ikke automatisk skrivetilgang.
    currentUserHarEmergencyAccess: emergencyAccess(state) || currentUserIsOwner(state),
    emergencyAccessPeople: emergencyAccessPeople(getPeopleForAccessControl(state), emergencyAccessList(state)),
    emergencyAccessList: emergencyAccessList(state),
  };
}
